import { useState } from 'react'
import { AlertTriangle, Ban, Loader2, ShieldCheck } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select } from '@/components/ui/input'
import { describeWriteError } from '@/features/users/permissions'
import { updateUserAccess } from '@/features/users/users-api'
import { PLAN_LABELS, ROLE_LABELS, USER_PLANS, USER_ROLES } from '@/lib/constants'
import type { UserPlan, UserProfile, UserRole } from '@/lib/types'
import { formatDateTime } from '@/lib/utils'

/**
 * Edits the only two fields the console is allowed to write on a profile. Everything else is shown
 * read-only: names and e-mails belong to their owner, and the rules would reject such a write anyway.
 */
export function UserAccessDialog({
  user,
  onClose,
}: {
  user: UserProfile
  onClose: () => void
}) {
  const [role, setRole] = useState<UserRole>(user.role)
  const [plan, setPlan] = useState<UserPlan>(user.plan ?? 'silver')
  const [isActive, setActive] = useState<boolean>(user.isActive)
  const [isSaving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const roleChanged = role !== user.role
  const planChanged = plan !== (user.plan ?? 'silver')
  const activeChanged = isActive !== user.isActive
  const hasChanges = roleChanged || planChanged || activeChanged

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await updateUserAccess(user.uid, {
        ...(roleChanged ? { role } : {}),
        ...(planChanged ? { plan } : {}),
        ...(activeChanged ? { isActive } : {}),
      })
      onClose()
    } catch (caught) {
      setError(describeWriteError(caught))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Доступ пользователя</DialogTitle>
          <DialogDescription>
            {user.email ?? user.uid}
            {user.displayName ? ` · ${user.displayName}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="text-muted-foreground grid gap-1 text-xs">
            <div>
              UID: <span className="font-mono">{user.uid}</span>
            </div>
            <div>Регистрация: {formatDateTime(user.createdAt)}</div>
          </div>

          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">Роль</span>
            <Select value={role} onChange={(event) => setRole(event.target.value as UserRole)}>
              {USER_ROLES.map((value) => (
                <option key={value} value={value}>
                  {ROLE_LABELS[value]}
                </option>
              ))}
            </Select>
          </label>

          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">Тариф</span>
            <Select value={plan} onChange={(event) => setPlan(event.target.value as UserPlan)}>
              {USER_PLANS.map((value) => (
                <option key={value} value={value}>
                  {PLAN_LABELS[value]}
                </option>
              ))}
            </Select>
            {user.plan === null ? (
              <span className="text-muted-foreground text-xs">
                В профиле тариф не задан — при сохранении будет записан выбранный.
              </span>
            ) : null}
          </label>

          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">Доступ к приложению</span>
            <Select
              value={isActive ? 'active' : 'blocked'}
              onChange={(event) => setActive(event.target.value === 'active')}
            >
              <option value="active">Активен</option>
              <option value="blocked">Заблокирован</option>
            </Select>
          </label>

          {roleChanged && role === 'admin' ? (
            <Alert variant="warning">
              <ShieldCheck />
              <AlertTitle>Вы делаете пользователя администратором</AlertTitle>
              <AlertDescription>
                Он получит доступ ко всем общим данным: материалы, правила расчёта, сообщения,
                список пользователей — и сможет менять роли других, включая вашу.
              </AlertDescription>
            </Alert>
          ) : null}

          {roleChanged && role === 'user' ? (
            <Alert variant="warning">
              <AlertTriangle />
              <AlertTitle>Вы снимаете права администратора</AlertTitle>
              <AlertDescription>
                Пользователь потеряет доступ к админ-разделам приложения и этой консоли.
              </AlertDescription>
            </Alert>
          ) : null}

          {activeChanged && !isActive ? (
            <Alert variant="destructive">
              <Ban />
              <AlertTitle>Вы блокируете пользователя</AlertTitle>
              <AlertDescription>
                При следующем открытии мобильного приложения он увидит экран «Пользователь
                заблокирован» и не сможет ничего сохранить в облако. Данные не удаляются —
                блокировка снимается здесь же.
              </AlertDescription>
            </Alert>
          ) : null}

          {activeChanged && isActive ? (
            <Alert variant="warning">
              <ShieldCheck />
              <AlertTitle>Вы разблокируете пользователя</AlertTitle>
              <AlertDescription>
                Доступ вернётся сразу, без перезапуска приложения.
              </AlertDescription>
            </Alert>
          ) : null}

          {error ? (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>Не сохранено</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Отмена
          </Button>
          <Button onClick={() => void handleSave()} disabled={!hasChanges || isSaving}>
            {isSaving ? <Loader2 className="animate-spin" /> : null}
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
